"use client";

import * as Sentry from "@sentry/nextjs";
import { useEffect } from "react";
import Link from "next/link";

/**
 * Root route-level error boundary for the App Router.
 *
 * Next.js renders this when a server component or client component below the
 * root layout throws during render. `app/global-error.jsx` is a level above and
 * covers catastrophic layout failures. Keep the UI minimal and safe: no data
 * fetching, no heavy dependencies.
 */
export default function GlobalRouteError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    Sentry.withScope((scope) => {
      if (error.digest) scope.setTag("digest", error.digest);
      Sentry.captureException(error);
    });
  }, [error]);

  return (
    <div className="min-h-dvh flex items-center justify-center bg-background dark:bg-background px-6">
      <div className="max-w-md text-center">
        <div className="mb-8">
          <div className="w-24 h-24 mx-auto mb-6 bg-neutral-100 dark:bg-neutral-900 rounded-lg flex items-center justify-center">
            <svg
              className="w-12 h-12 text-neutral-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
              />
            </svg>
          </div>
        </div>

        <h1 className="text-2xl font-semibold text-neutral-900 dark:text-neutral-50 mb-3">
          Something went wrong
        </h1>

        <p className="text-neutral-600 dark:text-neutral-400 mb-6">
          We&apos;ve been notified and are looking into it. You can try again or
          head back to the homepage.
        </p>

        {error.digest ? (
          <p className="text-xs text-neutral-400 mb-6 font-mono">
            Reference: {error.digest}
          </p>
        ) : null}

        <div className="flex items-center justify-center gap-3">
          <button
            type="button"
            onClick={reset}
            className="inline-flex items-center justify-center rounded-full bg-black text-white px-6 py-3 text-sm font-medium hover:bg-neutral-800 transition-colors"
          >
            Try again
          </button>
          <Link
            href="/"
            className="inline-flex items-center justify-center rounded-full border border-neutral-300 dark:border-neutral-700 px-6 py-3 text-sm font-medium text-neutral-900 dark:text-neutral-100 hover:bg-neutral-100 dark:hover:bg-neutral-900 transition-colors"
          >
            Home
          </Link>
        </div>
      </div>
    </div>
  );
}
